/**
 * Sprint 29 — Parser de ubicaciones que el cliente manda por WhatsApp.
 *
 * Función PURA (sin red, sin DOM): la usan el renderer (MapaPicker) y el
 * proceso main (ubicacionService, al resolver enlaces cortos).
 *
 * No usa Google Maps SDK ni API key — solo lee texto/URLs. El mapa que se
 * muestra sigue siendo Leaflet + OpenStreetMap (ADR "cero Google Maps").
 *
 * Formatos reconocidos:
 *  - Coordenadas planas:           `17.98, -92.92`
 *  - URL Google con `!3d!4d`:      `.../data=...!3d17.98!4d-92.92`
 *  - URL Google con query:         `?q=17.98,-92.92`, `q=loc:..`, `ll=`, `destination=`, `daddr=`, `center=`, `sll=`
 *  - URL Google con `@`:           `/maps/@17.98,-92.92,17z`
 *  - URL Google `/place/` `/dir/`: `/maps/place/17.98,-92.92`
 *  - Enlace corto:                 `maps.app.goo.gl/..`, `goo.gl/maps/..`, `g.co/..`
 *    → se devuelve para que el proceso main siga el redirect.
 */

export type ResultadoUbicacion =
  | { tipo: 'coords'; lat: number; lng: number }
  | { tipo: 'enlace_corto'; url: string }
  | { tipo: 'no_reconocido' };

/** Hosts de enlaces cortos que el proceso main puede resolver (whitelist anti-SSRF). */
export const HOSTS_ENLACE_CORTO = ['maps.app.goo.gl', 'goo.gl', 'g.co'] as const;

/**
 * Coordenadas plausibles. Descarta `0,0`: para un negocio en Tabasco eso
 * siempre es ruido de un parseo fallido, nunca una ubicación real.
 */
export function coordsValidas(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180 &&
    !(lat === 0 && lng === 0)
  );
}

function aCoords(a: string, b: string): { lat: number; lng: number } | null {
  const lat = Number(a);
  const lng = Number(b);
  return coordsValidas(lat, lng) ? { lat, lng } : null;
}

/** Patrón de un número de coordenada: opcional signo, 1-3 enteros, decimales opcionales. */
const NUM = '(-?\\d{1,3}(?:\\.\\d+)?)';

/**
 * Extrae coordenadas de un texto (URL larga de Google o par de coordenadas
 * plano). Devuelve null si no encuentra coords plausibles.
 */
export function extraerCoords(texto: string): { lat: number; lng: number } | null {
  // Decodifica %2C, %2F, etc. para que los patrones de query funcionen aunque
  // la URL venga escapada (caso típico del param `continue=` de Google).
  let t = texto;
  try {
    t = decodeURIComponent(texto);
  } catch {
    // Texto con `%` suelto: se trabaja sobre el crudo.
  }

  // Prioridad 1 — `!3d<lat>!4d<lng>`: las coords del LUGAR en URLs de Google.
  let m = t.match(new RegExp(`!3d${NUM}!4d${NUM}`));
  if (m) {
    const r = aCoords(m[1], m[2]);
    if (r) return r;
  }

  // Prioridad 2 — parámetros de query que llevan el punto exacto.
  m = t.match(
    new RegExp(
      `[?&](?:q|query|ll|destination|center|daddr|sll|viewpoint)=(?:loc:)?${NUM}(?:,|%2C|\\s)+${NUM}`,
      'i',
    ),
  );
  if (m) {
    const r = aCoords(m[1], m[2]);
    if (r) return r;
  }

  // Prioridad 3 — `@<lat>,<lng>`: centro del viewport en URLs de Google.
  m = t.match(new RegExp(`@${NUM},${NUM}`));
  if (m) {
    const r = aCoords(m[1], m[2]);
    if (r) return r;
  }

  // Prioridad 4 — `/place/<lat>,<lng>` o `/dir/.../<lat>,<lng>`.
  m = t.match(new RegExp(`/(?:place|dir)/(?:[^/]*/)*${NUM},${NUM}`));
  if (m) {
    const r = aCoords(m[1], m[2]);
    if (r) return r;
  }

  // Prioridad 5 — el texto es (casi) sólo un par de coordenadas planas.
  m = t.match(new RegExp(`^\\s*${NUM}\\s*[,;]\\s*${NUM}\\s*$`));
  if (m) {
    const r = aCoords(m[1], m[2]);
    if (r) return r;
  }

  return null;
}

/**
 * Punto de entrada. Clasifica lo que el operador pegó.
 */
export function parseUbicacion(textoRaw: string): ResultadoUbicacion {
  const texto = (textoRaw ?? '').trim();
  if (!texto) return { tipo: 'no_reconocido' };

  // ¿Hay una URL de host corto? Esos enlaces NO traen coords: son un redirect
  // que el proceso main debe seguir.
  const urlMatch = texto.match(/https?:\/\/[^\s]+/i);
  if (urlMatch) {
    try {
      const u = new URL(urlMatch[0]);
      if ((HOSTS_ENLACE_CORTO as readonly string[]).includes(u.hostname.toLowerCase())) {
        return { tipo: 'enlace_corto', url: urlMatch[0] };
      }
    } catch {
      // No era una URL válida; se intenta extraer coords del texto crudo.
    }
  }

  const coords = extraerCoords(texto);
  if (coords) return { tipo: 'coords', lat: coords.lat, lng: coords.lng };

  return { tipo: 'no_reconocido' };
}
